import {
  settingsMenuConfig,
  type SettingsMenuContext,
  type SettingsMenuItemConfig,
} from '@/features/settings/settings-menu.config';
import { scoreSettingsEntry } from '@/features/settings/settings-search';
import {
  settingsSearchProviders,
  type SettingsSearchEntry,
  type SettingsSearchProvider,
} from '@/features/settings/settings-search.config';
import { registerPendingSettingsDeepLink } from '@/features/settings/settings-search.registry';

import { Section } from './actions';

import type { Action } from './actions/types';

// Keeps entry rows below the section actions (kbar priority 1 after
// `actionToKBar`) so a section-name query surfaces the section itself first
const SETTINGS_ENTRY_PRIORITY = -0.5;

/**
 * Palette actions for the settings sections with the settings-search entries
 * nested under their section action, gated through the same context as the
 * sidebar search. Sections keep their `perform` — kbar runs it instead of
 * drilling into children when both are present — so entries only surface via
 * query matches, never in the palette's root list. Selecting an entry opens
 * the section with the entry as its deep-link item so `useSettingsDeepLink`
 * scrolls to and highlights the row
 */
export const buildSettingsActions = (
  entries: SettingsSearchEntry[],
  ctx: SettingsMenuContext,
  openSection: (section: string, item?: string) => void,
  sections: SettingsMenuItemConfig[] = settingsMenuConfig
): Action[] =>
  sections
    .filter((item) => !item.enabled || item.enabled(ctx))
    .map((item) => {
      const sectionId = item.hash.replace('settings/', '');
      const entryActions: Action[] = entries
        .filter(
          (entry) =>
            entry.sectionId === sectionId &&
            (!entry.enabled || entry.enabled(ctx))
        )
        .map((entry) => ({
          id: `settings-search.${entry.id}`,
          name: entry.label,
          subtitle: entry.description,
          keywords: entry.keywords?.join(','),
          section: Section.Settings,
          priority: SETTINGS_ENTRY_PRIORITY,
          match: (query) => scoreSettingsEntry(entry, query),
          perform: () => {
            registerPendingSettingsDeepLink(entry);
            openSection(sectionId, entry.id);
          },
        }));
      return {
        id: item.title.toLowerCase().replace(/\s+/g, '-'),
        name: item.title,
        section: Section.Settings,
        match: (query) => scoreSettingsEntry({ label: item.title }, query),
        perform: () => openSection(sectionId),
        children: entryActions,
      };
    });

/**
 * Resolve the dynamic settings-search providers (keyboard shortcuts, MCP
 * clients, archived chats), delivering each provider's entries to `onEntries`
 * as it settles. A failed provider leaves its previous snapshot untouched; the
 * caller still guards resolutions from stale runs.
 */
export const resolveSettingsProviderEntries = (
  ctx: SettingsMenuContext,
  onEntries: (
    provider: SettingsSearchProvider,
    entries: SettingsSearchEntry[]
  ) => void,
  providers: SettingsSearchProvider[] = settingsSearchProviders
): void => {
  for (const provider of providers) {
    provider
      .getEntries(ctx)
      .then((entries) => onEntries(provider, entries))
      .catch((error) => {
        console.error('Settings search provider failed:', error);
      });
  }
};
